import { useEffect, useState, useCallback } from 'react';
import { api } from '../../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Loader2, TrendingUp, TrendingDown, Minus } from 'lucide-react';

/**
 * Form Doluluk Mini-Grafik (Faz 4.6 — Şub 03, 2026)
 * --------------------------------------------------------------
 * Tarih aralığındaki günlük doluluk yüzdelerini şirket bazlı bar
 * grafikleri ile gösterir. Recharts gerekmiyor — saf CSS/SVG.
 */
const fmtDay = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
};

const pctColor = (p) => {
  if (p == null) return 'bg-slate-200 dark:bg-slate-700';
  if (p >= 90) return 'bg-emerald-500';
  if (p >= 70) return 'bg-amber-400';
  if (p >= 40) return 'bg-orange-400';
  return 'bg-rose-500';
};

function Bar({ d, height = 56 }) {
  const p = d.pct;
  const h = p == null ? 4 : Math.max(4, Math.round((p / 100) * height));
  const color = pctColor(p);
  return (
    <div className="flex flex-col items-center gap-1 group relative" style={{ width: 30 }}>
      <div className="relative flex items-end" style={{ height }}>
        <div className={`w-5 rounded-t transition-all ${color}`} style={{ height: h }} />
      </div>
      <span className="text-[9px] text-slate-400">{fmtDay(d.date).slice(0, 5)}</span>
      {/* Tooltip */}
      <div className="absolute -top-9 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-[10px] px-1.5 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-10 shadow-lg">
        {p == null ? 'Veri yok' : `%${p}`} ({d.submitted}/{d.total})
      </div>
    </div>
  );
}

function TrendIcon({ days }) {
  // İlk yarı vs ikinci yarı ortalama farkı
  const valid = days.filter((x) => x.pct != null);
  if (valid.length < 4) return <Minus className="h-3.5 w-3.5 text-slate-300" />;
  const mid = Math.floor(valid.length / 2);
  const first = valid.slice(0, mid);
  const second = valid.slice(mid);
  const avg = (arr) => arr.reduce((a, b) => a + b.pct, 0) / arr.length;
  const diff = avg(second) - avg(first);
  if (diff > 5) return <TrendingUp className="h-3.5 w-3.5 text-emerald-500" title={`+%${Math.round(diff)}`} />;
  if (diff < -5) return <TrendingDown className="h-3.5 w-3.5 text-rose-500" title={`%${Math.round(diff)}`} />;
  return <Minus className="h-3.5 w-3.5 text-slate-300" title="Stabil" />;
}

export default function FormCompletionChart({ dateFrom, dateTo }) {
  // null = loading, false = error/empty, object = data
  const [data, setData] = useState(null);

  const reload = useCallback(async () => {
    try {
      const r = await api.get(`/planning/form-submissions/stats`, { params: { date_from: dateFrom, date_to: dateTo } });
      setData(r.data?.data || false);
    } catch {
      setData(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    setData(null);
    reload();
  }, [reload]);

  if (data === null) {
    return (
      <Card>
        <CardContent className="py-6 flex items-center justify-center gap-2 text-sm text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Grafik yükleniyor…
        </CardContent>
      </Card>
    );
  }

  if (!data || !data.by_day || data.by_day.length === 0) {
    return (
      <Card>
        <CardContent className="py-6 text-center text-sm text-slate-400">
          Form şablonu olan depolarda bu aralıkta atama bulunmuyor.
        </CardContent>
      </Card>
    );
  }

  const totalTotal = data.by_day.reduce((s, d) => s + d.total, 0);
  const totalSub = data.by_day.reduce((s, d) => s + d.submitted, 0);
  const overall = totalTotal ? Math.round((totalSub / totalTotal) * 100) : null;

  return (
    <Card data-testid="form-completion-chart">
      <CardHeader className="py-3 flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-sm flex items-center gap-2">
          Form Doluluk Trendi
          <span className="text-[10px] font-normal text-slate-400">son {data.by_day.length} gün</span>
        </CardTitle>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Genel</div>
          <div className={`text-xl font-bold ${overall == null ? 'text-slate-400' : overall >= 90 ? 'text-emerald-600' : overall >= 70 ? 'text-amber-500' : 'text-rose-500'}`} data-testid="completion-overall-pct">
            {overall == null ? '—' : `%${overall}`}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 pb-4 space-y-3">
        {/* Overall chart */}
        <div className="rounded-lg bg-slate-50 dark:bg-slate-800/40 p-3">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-2 flex items-center gap-1">
            Tüm Şirketler · {totalSub}/{totalTotal} dolduruldu
          </div>
          <div className="flex items-end gap-1.5 overflow-x-auto pb-1">
            {data.by_day.map((d) => <Bar key={d.date} d={d} />)}
          </div>
        </div>

        {/* Per-company breakdown */}
        {data.by_company.length > 1 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.by_company.map((c) => (
              <div key={c.company_id} className="rounded-lg border border-slate-200 dark:border-slate-700 p-3" data-testid={`completion-company-${c.company_id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate">{c.company_name}</span>
                    <TrendIcon days={c.by_day} />
                  </div>
                  <div className={`text-sm font-bold ${c.pct == null ? 'text-slate-400' : c.pct >= 90 ? 'text-emerald-600' : c.pct >= 70 ? 'text-amber-500' : 'text-rose-500'}`}>
                    {c.pct == null ? '—' : `%${c.pct}`}
                    <span className="text-[10px] font-normal text-slate-400 ml-1">({c.submitted}/{c.total})</span>
                  </div>
                </div>
                <div className="flex items-end gap-1 overflow-x-auto pb-1">
                  {c.by_day.map((d) => <Bar key={d.date} d={d} height={36} />)}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Legend */}
        <div className="flex items-center gap-3 text-[10px] text-slate-500 pt-1">
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500 inline-block" /> ≥%90</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-amber-400 inline-block" /> ≥%70</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-orange-400 inline-block" /> ≥%40</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-rose-500 inline-block" /> &lt;%40</span>
        </div>
      </CardContent>
    </Card>
  );
}
